import React from "react";
import { Button, HStack, Heading, useToast, VStack } from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { CInput } from "../../components";
import { useFormik } from "formik";
import { loginSchema } from "../../utils/schemas";
import { AuthModel } from "../../services/models";
import { useAuth } from "../../context/AuthContext";
import { sessionManager } from "../../utils/sessionManager";

export default function Login() {
  const navigate = useNavigate();
  const toast = useToast();
  const { toggleLogin } = useAuth();

  const onLogin = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      let response = await AuthModel.login(values);
      AuthModel.setUser(response.data.user);
      AuthModel.storeToken(response.data.token);
      // Reset session expiration state
      sessionManager.reset();
      // Update auth context - do this before navigation
      toggleLogin(true);
      // Small delay to ensure state is updated before navigation
      // This prevents race condition with ProtectedLayoutRoute
      await new Promise(resolve => setTimeout(resolve, 0));
      if (response.data.user.role === "admin") {
        navigate("/");
      } else {
        navigate("/all-orders");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast({
          description: error.response.data.message,
          status: "error",
          isClosable: true,
        });
      } else {
        toast({
          description: "Something went wrong!",
          status: "error",
          isClosable: true,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: onLogin,
  });

  return (
    <>
      <Heading
        as="h1"
        w="full"
        textAlign={{ base: "center", lg: "start" }}
        mb="4"
      >
        Login
      </Heading>
      <form
        onSubmit={formik.handleSubmit}
        noValidate
        aria-label="Login form"
        id="login-form"
        style={{ width: "100%" }}
      >
        <VStack spacing={1} align="stretch" w="full">
          <CInput
            label="Email"
            type="email"
            name="email"
            id="email"
            autoComplete="email"
            placeholder="Enter Email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errors={formik.errors.email}
            touched={formik.touched.email}
            required
            aria-required="true"
          />
          <CInput
            label="Password"
            type="password"
            name="password"
            id="password"
            autoComplete="current-password"
            placeholder="Enter Password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errors={formik.errors.password}
            touched={formik.touched.password}
            required
            aria-required="true"
          />
        </VStack>
        <HStack justifyContent="end" w="full">
          <Link
            to="/forgot-password"
            aria-label="Forgot password? Reset your password"
            style={{
              fontWeight: "600",
              marginTop: "10px",
              pointerEvents: formik.isSubmitting ? "none" : "auto",
            }}
            tabIndex={formik.isSubmitting ? -1 : 0}
          >
            Forgot Password?
          </Link>
        </HStack>
        <Button
          type="submit"
          isLoading={formik.isSubmitting}
          loadingText="Logging in..."
          bgColor="black"
          w="full"
          borderRadius="lg"
          fontWeight="medium"
          py="6"
          my="2"
          disabled={formik.isSubmitting}
          aria-disabled={formik.isSubmitting}
        >
          Login
        </Button>
      </form>
    </>
  );
}
